import { useState } from "react";
import { Wand2, Loader2, Inbox, AlertTriangle, CheckCircle2, XCircle, Lightbulb, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AnalysisFactor {
  factor: string;
  impact: "positive" | "negative";
  explanation: string;
}

interface AnalysisResult {
  prediction: "Primary" | "Promotions";
  confidence: number;
  factors: AnalysisFactor[];
  suggestions: string[];
  summary: string;
}

const sampleEmailHtml = `<!DOCTYPE html>
<html>
<head>
  <style>
    .container { max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif; }
    .header { background: #4F46E5; color: white; padding: 20px; text-align: center; }
    .cta-button { background: #10B981; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px; display: inline-block; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>🎉 Exclusive Sale - 50% OFF!</h1>
    </div>
    <div style="padding: 20px;">
      <p>Hi there,</p>
      <p>Don't miss our biggest sale of the year! Use code <strong>SAVE50</strong> at checkout.</p>
      <p style="text-align: center; margin: 30px 0;">
        <a href="https://example.com/shop?utm_source=email" class="cta-button">Shop Now</a>
      </p>
      <p>Limited time only - offer expires in 24 hours!</p>
      <img src="https://tracking.example.com/pixel.gif" width="1" height="1" style="display:none;" />
    </div>
    <div style="background: #f5f5f5; padding: 15px; text-align: center; font-size: 12px; color: #666;">
      <a href="https://example.com/unsubscribe">Unsubscribe</a> | 
      <a href="https://example.com/preferences">Manage Preferences</a>
    </div>
  </div>
</body>
</html>`;

export const GmailPromotionsAnalyzer = () => {
  const [emailHtml, setEmailHtml] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [copied, setCopied] = useState(false);

  const analyzeEmail = async () => {
    if (!emailHtml.trim()) {
      toast.error("Please enter email HTML to analyze");
      return;
    }

    setIsAnalyzing(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("analyze-email-html", {
        body: { emailHtml },
      });

      if (error) {
        throw error;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      setResult(data);
      toast.success("Analysis complete!");
    } catch (error: any) {
      console.error("Analysis error:", error);
      if (error.message?.includes("429") || error.message?.includes("Rate limit")) {
        toast.error("Rate limit exceeded. Please try again in a moment.");
      } else if (error.message?.includes("402") || error.message?.includes("credits")) {
        toast.error("AI credits exhausted. Please add credits to continue.");
      } else {
        toast.error(error.message || "Failed to analyze email. Please try again.");
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const loadSample = () => {
    setEmailHtml(sampleEmailHtml);
    setResult(null);
    toast.success("Sample promotional email loaded");
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(emailHtml);
    setCopied(true);
    toast.success("HTML copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="py-8 sm:py-10">
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Wand2 className="w-5 h-5 text-primary" />
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground">
            Email Tab Predictor
          </h2>
        </div>
        <p className="text-muted-foreground">
          Paste your email HTML to predict whether it will land in Primary or Promotions
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">Email HTML</label>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadSample}>
                Load Sample
              </Button>
              {emailHtml && (
                <Button variant="ghost" size="sm" onClick={handleCopy}>
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              )}
            </div>
          </div>
          <Textarea
            value={emailHtml}
            onChange={(e) => setEmailHtml(e.target.value)}
            placeholder="Paste your email HTML here..."
            className="min-h-[300px] font-mono text-xs"
          />
          <Button
            onClick={analyzeEmail}
            disabled={isAnalyzing || !emailHtml.trim()}
            className="w-full"
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4 mr-2" />
                Analyze Email
              </>
            )}
          </Button>
        </div>

        {/* Results Section */}
        <div className="space-y-4">
          {!result && !isAnalyzing && (
            <div className="bg-muted/30 border border-border rounded-xl p-8 flex flex-col items-center justify-center min-h-[300px] text-center">
              <Inbox className="w-12 h-12 text-muted-foreground/50 mb-4" />
              <h3 className="font-semibold text-foreground mb-2">No Analysis Yet</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Paste your email HTML and click "Analyze Email" to get a prediction
              </p>
            </div>
          )}

          {isAnalyzing && (
            <div className="bg-muted/30 border border-border rounded-xl p-8 flex flex-col items-center justify-center min-h-[300px]">
              <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
              <h3 className="font-semibold text-foreground mb-2">Analyzing...</h3>
              <p className="text-sm text-muted-foreground">
                AI is reviewing your email HTML
              </p>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              {/* Prediction Card */}
              <div className={`rounded-xl p-5 border ${
                result.prediction === "Primary" 
                  ? "bg-success/10 border-success/30" 
                  : "bg-warning/10 border-warning/30"
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      result.prediction === "Primary" ? "bg-success/20" : "bg-warning/20"
                    }`}>
                      {result.prediction === "Primary" ? (
                        <Inbox className="w-5 h-5 text-success" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-warning" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-foreground">
                        Predicted: {result.prediction}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {result.confidence}% confidence
                      </p>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    result.prediction === "Primary"
                      ? "bg-success/20 text-success"
                      : "bg-warning/20 text-warning"
                  }`}>
                    {result.prediction}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{result.summary}</p>
              </div>

              {/* Factors */}
              <div className="bg-card border border-border rounded-xl p-5">
                <h4 className="font-semibold text-foreground mb-3">Classification Factors</h4>
                <div className="space-y-2">
                  {result.factors.map((factor, index) => (
                    <div key={index} className="flex items-start gap-2">
                      {factor.impact === "positive" ? (
                        <CheckCircle2 className="w-4 h-4 text-success shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                      )}
                      <div className="text-sm">
                        <span className="font-medium text-foreground">{factor.factor}:</span>{" "}
                        <span className="text-muted-foreground">{factor.explanation}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Suggestions */}
              {result.suggestions.length > 0 && (
                <div className="bg-info/5 border border-info/20 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="w-5 h-5 text-info" />
                    <h4 className="font-semibold text-foreground">Suggestions</h4>
                  </div>
                  <ul className="space-y-2">
                    {result.suggestions.map((suggestion, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-info">•</span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-muted/30 border border-border rounded-xl">
        <p className="text-xs text-muted-foreground">
          <strong>Disclaimer:</strong> This tool provides predictions based on known Gmail classification patterns. 
          Actual placement may vary as Gmail's algorithm is personalized per user and constantly evolving. 
          Use as guidance, not guarantee.
        </p>
      </div>
    </section>
  );
};
